<template>
  <div class="col px-0">
  <section class="card bg-white rounded mb-4 p-3 p-sm-4">
    <h1 class="h2 mb-3">Horses</h1>
    <p class="text-muted">
      Horses this member (or another member on the same account) owns or rides.
      <span class="badge alert-prim">Owner</span> / <span class="badge alert-secondary">Rider</span>
      mark this member's own direct link to the horse; <span class="badge alert-dark">Family</span>
      means they can ride it only because a family member is its direct owner/rider.
    </p>

    <filter-bar :active="hasActiveFilters" @clear="clearAll">
      <filter-multiselect
        label="Zone"
        option-label="code"
        placeholder="Any zone"
        :value="filters.zone_id"
        :options="zoneOptions"
        @input="ids => setFilter('zone_id', ids)"
        @clear="clearFilter('zone_id')"
      />
      <filter-multiselect
        label="Status"
        placeholder="Any status"
        :value="filters.status_id"
        :options="statusOptions"
        @input="ids => setFilter('status_id', ids)"
        @clear="clearFilter('status_id')"
      />
      <filter-multiselect
        label="BoY Status"
        placeholder="Any BoY status"
        :value="filters.boy_status_id"
        :options="statusOptions"
        @input="ids => setFilter('boy_status_id', ids)"
        @clear="clearFilter('boy_status_id')"
      />
      <horse-member-relation-filter
        :value="filters.relation"
        @input="ids => setFilter('relation', ids)"
        @clear="clearFilter('relation')"
      />
    </filter-bar>

    <div class="form-inline mb-sm-3 d-flex justify-content-between">
      <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-0">
        <label for="memberHorsesRegFilter" class="sr-only">Filter</label>
        <div class="filter">
          <div class="form-check pl-0" v-for="tab in tabs" :key="tab.value">
            <input
              class="form-check-input"
              type="radio"
              name="memberHorsesShowRegistrationState"
              :id="'memberHorsesShowReg-' + tab.value"
              :value="tab.value"
              :checked="filters.registration_state === tab.value"
              @change="onTabChange(tab.value)"
            >
            <label class="form-check-label" :for="'memberHorsesShowReg-' + tab.value">{{ tab.label }}</label>
          </div>
        </div>
      </div>

      <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 ml-auto px-0 d-flex align-items-center">
        <label for="memberHorsesSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
        <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
          <input
            id="memberHorsesSearch"
            v-model="searchInput"
            type="text"
            class="form-control mb-0 flex-grow-1 flex-md-grow-0"
            @keyup.enter="applySearch"
            @search="applySearch"
          >
          <svg
            class="append"
            role="button"
            aria-label="Search"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 14 14"
            width="16"
            height="16"
            @click="applySearch"
          >
            <rect width="100%" height="100%" fill="none" />
            <g>
              <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
              <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
            </g>
          </svg>
        </div>
      </div>
    </div>

    <div class="position-relative" style="min-height: 240px">
      <responsive-table
        :columns="columns"
        :rows="horses"
        :loading="loading"
        :breakpoints="breakpoints"
        :sort="filters.sort"
        :direction="filters.direction"
        empty-text="No horses found."
        @sort="setSort"
      >
        <template #cell:name="{ row }">
          <list-name-cell
            align="right"
            :label="row.name"
            :to="{ name: profileRoute, params: { slug: row.id } }"
          >
            <template #badges>
              <span class="badge" :class="row.active ? 'alert-success' : 'alert-warning'">{{ row.active ? 'Active' : 'Archived' }}</span>
              <span class="badge" :class="row.registrationDate ? 'alert-info' : 'alert-warning'">{{ row.registrationDate ? 'Registered' : 'Unregistered' }}</span>
              <horse-member-relation-badges :is-owner="row.isOwner" :is-rider="row.isRider" />
            </template>
          </list-name-cell>
        </template>

        <template #cell:owner_name="{ row }">
          <span>
            <router-link
              v-if="row.ownerId"
              :to="{ name: 'Member Profile', params: { slug: row.ownerId } }"
              class="text-primary"
            >{{ row.ownerName }}</router-link>
            <template v-else>{{ row.ownerName }}</template>
            <span v-if="row.ownerDisplayName" class="text-muted"> ({{ row.ownerDisplayName }})</span>
          </span>
        </template>
        <template #cell:zone_id="{ row }">{{ zoneCode(row.zoneId) }}</template>
        <template #cell:beginning_of_year_status="{ row }">{{ row.beginningOfYearStatus }}</template>
        <template #cell:horse_status_id="{ row }">{{ statusName(row.horseStatusId) }}</template>
      </responsive-table>
    </div>

    <!-- Same markup/classes as the legacy DataTable footer (DataTableFields/ShowEntries.vue) -->
    <div class="form-inline justify-content-between align-items-start mt-3">
      <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
        <label for="memberHorsesShow" class="mr-2 mb-0 font-weight-normal">Show</label>
        <select
          id="memberHorsesShow"
          class="form-control mr-2"
          :value="filters.per_page"
          @change="setPerPage(Number($event.target.value))"
        >
          <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
        </select>
        <span>Entries</span>
      </div>

      <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
        <pagination
          :total-pages="totalPages"
          :page="page"
          :on-change="setPage"
          :disabled="loading"
        />
      </div>
    </div>
  </section>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import FilterBar from '@/components/filters/FilterBar'
import FilterMultiselect from '@/components/filters/FilterMultiselect'
import HorseMemberRelationFilter from '@/components/filters/HorseMemberRelationFilter'
import HorseMemberRelationBadges from '@/components/HorseMemberRelationBadges'
import ListNameCell from '@/components/ListNameCell'
import useHorsesList from '@/composables/horses/useHorsesList'
import useUserRole from '@/composables/useUserRole'
import { buildColumns, buildBreakpoints, REGISTRATION_TABS } from '@/views/Horses/Listing/columns'

export default defineComponent({
  name: 'MemberProfileHorses',
  components: { ResponsiveTable, Pagination, FilterBar, FilterMultiselect, HorseMemberRelationFilter, HorseMemberRelationBadges, ListNameCell },
  props: ['memberId'],
  setup(props) {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store
    const { isAdmin } = useUserRole()

    const memberId = computed(() => props.memberId)
    const {
      horses, loading, page, totalPages, filters, perPageOptions,
      fetch, setPage, setSort, setPerPage, setSearch, setFilter, clearFilter, clearFilters
    } = useHorsesList(false, memberId)

    const columns = buildColumns('secretary')
    const breakpoints = buildBreakpoints('secretary')
    const tabs = REGISTRATION_TABS
    // Every horse this member's account touches goes to their own /admin-horses or
    // /secretary-horses profile route, matching whichever the viewing admin/secretary has.
    const profileRoute = isAdmin ? 'Admin Horse Profile' : 'Secretary Horse Profile'

    const zoneOptions = computed(() => store.state.listZones || [])
    const statusOptions = computed(() => store.state.horseStatus || [])

    const hasActiveFilters = computed(() =>
      Boolean(filters.search)
      || filters.registration_state !== 'all'
      || filters.zone_id.length > 0
      || filters.status_id.length > 0
      || filters.boy_status_id.length > 0
      || filters.relation.length > 0
    )

    const searchInput = ref(filters.search || '')

    function zoneCode(id) {
      const zone = store.state.listZones.find(z => z.id === id)
      return zone ? zone.code : ''
    }
    function statusName(id) {
      const status = store.state.horseStatus.find(s => s.id === id)
      return status ? status.name : ''
    }

    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    function clearAll() {
      clearTimeout(searchTimer)
      searchInput.value = ''
      clearFilters()
    }

    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })
    onBeforeUnmount(() => clearTimeout(searchTimer))

    function onTabChange(value) {
      setFilter('registration_state', value)
    }

    onMounted(fetch)

    return {
      columns,
      breakpoints,
      tabs,
      profileRoute,
      horses,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      zoneOptions,
      statusOptions,
      hasActiveFilters,
      setPage,
      setSort,
      setPerPage,
      setFilter,
      clearFilter,
      clearAll,
      applySearch,
      onTabChange,
      zoneCode,
      statusName
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Members/Tabs/Horses.vue